import { useState, useEffect } from 'react';
import { LoginForm } from './LoginForm';
import { SignUpForm } from './SignUpForm';
import { Card, CardContent, CardDescription, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Archive, Sparkles, ArrowLeft, ShieldCheck, BookOpenText, LayoutDashboard } from 'lucide-react';
import { Button } from '../ui/button';

interface AuthPageProps {
  onBackToLanding?: () => void;
}

export function AuthPage({ onBackToLanding }: AuthPageProps) {
  const [activeTab, setActiveTab] = useState<'login' | 'signup'>('login');
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#f0f2f5] px-3 py-4 sm:px-4 sm:py-6 md:px-6 lg:px-10">
      {onBackToLanding && (
        <div className="absolute left-4 top-4 z-20 md:left-6 md:top-6">
          <Button
            variant="ghost"
            onClick={onBackToLanding}
            className="gap-2 rounded-full border border-slate-200 bg-white/90 px-4 text-slate-600 shadow-sm hover:bg-white hover:text-slate-900"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
        </div>
      )}

      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-20 top-12 h-72 w-72 rounded-full bg-[#1877F2]/12 blur-3xl" />
        <div className="absolute right-0 top-0 h-80 w-80 rounded-full bg-white blur-3xl" />
        <div className="absolute bottom-0 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-sky-200/40 blur-3xl" />
      </div>

      <div className={`relative z-10 mx-auto flex min-h-[calc(100vh-2rem)] w-full max-w-6xl items-center transition-all duration-700 ${
        isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
      }`}>
        <div className="grid w-full items-start gap-6 lg:grid-cols-[1.05fr_0.95fr] lg:items-center xl:gap-16">
          <section className="mx-auto hidden w-full max-w-2xl lg:mx-0 lg:block">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-blue-100 bg-white px-4 py-2 text-sm font-medium text-[#1877F2] shadow-sm">
              <Archive className="h-4 w-4" />
              AIRS platform access
            </div>
            <h1 className="max-w-xl font-['Segoe_UI'] text-4xl font-bold leading-tight tracking-tight text-[#1877F2] md:text-5xl lg:text-6xl">
              AIRS helps your department archive, verify, and schedule with less friction.
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-8 text-slate-600 md:text-xl">
              Experience a seamless and user-friendly gateway for students and faculty, combining the simplicity of modern auth design with a guided multi-step registration tailored for efficiency.
            </p>
          </section>

          <Card className="mx-auto w-full max-w-xl overflow-hidden border-0 bg-white shadow-[0_20px_60px_rgba(15,23,42,0.14)] lg:max-h-[calc(100vh-3rem)]">
            <div className="h-2 w-full bg-[#1877F2]" />
            <CardContent className="p-0 lg:overflow-y-auto">
              <div className="border-b border-slate-100 px-4 pb-4 pt-5 sm:px-5 md:px-6 md:pb-5 md:pt-6">
                <div className="mb-3 flex items-center gap-3 lg:hidden">
                  <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#1877F2] text-white shadow-sm">
                    <Archive className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#1877F2]">AIRS</p>
                    <p className="text-sm text-slate-500">Archive Intelligent Room Scheduler</p>
                  </div>
                </div>
                <CardTitle className="font-['Segoe_UI'] text-2xl font-bold text-slate-900 sm:text-3xl">Welcome back</CardTitle>
                <CardDescription className="mt-2 text-sm leading-6 text-slate-500">
                  Log in or create an account to continue to AIRS.
                </CardDescription>
              </div>

              <div className="px-3 pb-4 pt-3 sm:px-4 md:px-5 md:pb-5">
                <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'login' | 'signup')} className="w-full gap-3 sm:gap-4">
                  <TabsList className="grid h-11 w-full grid-cols-2 rounded-2xl bg-slate-100 p-1 sm:h-12">
                    <TabsTrigger
                      value="login"
                      className="rounded-xl px-2 text-sm font-semibold text-slate-500 data-[state=active]:bg-white data-[state=active]:text-[#1877F2] data-[state=active]:shadow-sm"
                    >
                      Log In
                    </TabsTrigger>
                    <TabsTrigger
                      value="signup"
                      className="rounded-xl px-2 text-sm font-semibold text-slate-500 data-[state=active]:bg-white data-[state=active]:text-[#1877F2] data-[state=active]:shadow-sm"
                    >
                      Create Account
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="login" className="mt-0">
                    <div className="px-1 pb-1 pt-1 sm:px-2 md:px-3">
                      <LoginForm />
                    </div>
                  </TabsContent>

                  <TabsContent value="signup" className="mt-0">
                    <div className="px-1 pb-1 pt-1 sm:px-2 md:px-3">
                      <SignUpForm onSuccess={() => setActiveTab('login')} />
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}